import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isNativePlatform, hapticNotification } from '@/lib/capacitor';

interface SwipeNavigationProps {
  children: React.ReactNode;
  onSwipeBack?: () => void;
}

export function SwipeNavigation({ children, onSwipeBack }: SwipeNavigationProps) {
  const [startX, setStartX] = useState<number>(0);
  const [currentX, setCurrentX] = useState<number>(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Only enable on native mobile
  if (!isNativePlatform()) {
    return <>{children}</>;
  }

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      // Only trigger if swipe starts from left edge (first 50px)
      if (e.touches[0].clientX < 50) {
        setStartX(e.touches[0].clientX);
        setIsSwiping(true);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isSwiping) return;
      
      const x = e.touches[0].clientX;
      const diff = x - startX;
      
      // Only allow right swipe
      if (diff > 0) {
        setCurrentX(diff);
      }
    };

    const handleTouchEnd = () => {
      if (!isSwiping) {
        setIsSwiping(false);
        return;
      }

      const diff = currentX - startX;
      
      // If swiped more than 100px, trigger back navigation
      if (diff > 100) {
        hapticNotification('SUCCESS').catch(() => {});
        if (onSwipeBack) {
          onSwipeBack();
        } else {
          window.history.back();
        }
      }

      setIsSwiping(false);
      setCurrentX(0);
      setStartX(0);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('touchstart', handleTouchStart);
      container.addEventListener('touchmove', handleTouchMove);
      container.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      if (container) {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [isSwiping, startX, currentX, onSwipeBack]);

  const swipeProgress = isSwiping ? Math.min(currentX / 100, 1) : 0;

  return (
    <div
      ref={containerRef}
      style={{
        transform: isSwiping ? `translateX(${currentX * 0.3}px)` : 'none',
        transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
        opacity: 1 - swipeProgress * 0.3,
      }}
    >
      {children}
      
      {/* Swipe indicator */}
      {isSwiping && swipeProgress > 0.2 && (
        <div
          className="fixed top-1/2 left-4 -translate-y-1/2 pointer-events-none z-50"
          style={{
            opacity: swipeProgress,
          }}
        >
          <div className="h-12 w-12 rounded-full bg-primary/20 backdrop-blur-sm flex items-center justify-center">
            <svg
              className="h-6 w-6 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}

